import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import NcmSearch from './NcmSearch';
import NcmCustomize from './NcmCustomize';
import NcmReview from './NcmReview';

const NcmModal = ({
  isOpen,
  onClose,
  activeStep,
  steps,
  searchTerm,
  displaySearchTerm,
  handleSearchInputChange,
  handleFormChange,
  handleSearch,
  isSearching,
  searchError,
  searchResults,
  selectedRow,
  handleSelectNcm,
  handleContinue,
  handleBack,
  handleSave,
  isSaving,
  formData,
  ncmDescription
}) => {
  // Helper function to get the title based on active step
  const getStepTitle = () => {
    switch (activeStep) {
      case 0:
        return 'Search for NCM Code';
      case 1:
        return 'Customize Tax Values';
      case 2:
        return 'Review NCM Configuration';
      default:
        return 'NCM Configuration';
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={isOpen}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: '70%', lg: '60%' },
          maxWidth: 800,
          maxHeight: '85vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: { xs: 2, sm: 3 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" component="h2">
              {getStepTitle()}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CancelIcon fontSize="small" />}
              onClick={onClose}
              sx={{ borderRadius: 1.5, minWidth: 0, px: 1.5 }}
            >
              Close
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Search */}
          {activeStep === 0 && (
            <NcmSearch
              searchTerm={searchTerm}
              displaySearchTerm={displaySearchTerm}
              handleInputChange={handleSearchInputChange}
              handleSearch={handleSearch}
              isSearching={isSearching}
              searchError={searchError}
              searchResults={searchResults}
              selectedRow={selectedRow}
              handleSelectNcm={handleSelectNcm}
              onContinue={handleContinue}
            />
          )}

          {/* Step 1: Customize */}
          {activeStep === 1 && (
            <NcmCustomize
              searchResults={searchResults}
              selectedRow={selectedRow}
              onBack={handleBack}
              onContinue={handleContinue}
              formData={formData}
              handleFormChange={handleFormChange}
              ncmDescription={ncmDescription}
            />
          )}

          {/* Step 2: Review */}
          {activeStep === 2 && (
            <NcmReview
              searchResults={searchResults}
              selectedRow={selectedRow}
              onBack={handleBack}
              formData={formData}
              ncmDescription={ncmDescription}
            />
          )}

          {/* Bottom action buttons - Only show Save button on final Review step */}
          {activeStep === 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                loading={isSaving}
                onClick={handleSave}
                disabled={!formData.ncm_code}
                sx={{ borderRadius: 1.5 }}
              >
                Save NCM
              </LoadingButton>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default NcmModal;
